import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Button } from '@/shared/ui/Button'
import { Field } from '@/shared/ui/Field'
import { Modal } from '@/shared/ui/Modal'
import { Pagination } from '@/shared/ui/Pagination'
import { Empty, Panel } from '@/shared/ui/Panel'
import { Strip } from '@/shared/ui/Strip'
import { CopyIcon } from '@/shared/ui/icons'
import type { ApiKeyResponse, WebhookDeliveryResponse } from '@/shared/api/types'
import { useStores } from '@/stores/RootStore'
import styles from './Admin.module.css'

/**
 * Интеграция мерчанта на его карточке: ключи API, приёмник вебхуков,
 * журнал доставок.
 *
 * В макете этого раздела нет — он появился вместе с API для магазинов,
 * которое макет не предусматривал. Без него подключить мерчанта можно
 * только вызовами API с токеном администратора, то есть не человеку,
 * а разработчику. Заказчик подключает мерчантов сам.
 *
 * Ключ и секрет показываются ОДИН РАЗ, в диалоге сразу после выдачи.
 * Закрыл — больше не увидит никто, включая нас: в базе только хеш.
 */
export const AdminIntegration = observer(function AdminIntegration({ accountId }: { accountId: string }) {
  const { integration: store, ui } = useStores()

  useEffect(() => {
    store.open(accountId)
  }, [accountId, store])

  const copy = async (text: string, done: string) => {
    try {
      await navigator.clipboard.writeText(text)
      ui.success(done)
    } catch {
      ui.failure('Не удалось скопировать')
    }
  }

  const hook = store.webhook.value
  const keys = store.keys.value ?? []
  const deliveries = store.deliveries.value

  return (
    <>
      <Panel title="Ключи API">
        <div className={styles.intForm}>
          <Field placeholder="Название ключа"
                 value={store.keyName}
                 onChange={(e) => store.setKeyName(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') void store.issueKey() }} />
          <Button variant="primary" busy={store.busy}
                  disabled={store.keyName.trim() === ''}
                  onClick={() => void store.issueKey()}>
            Выдать ключ
          </Button>
        </div>

        {keys.length > 0 ? (
          <ul className={styles.intList}>
            {keys.map((key) => <KeyRow key={key.id} item={key} onRevoke={() => void store.revokeKey(key)} busy={store.busy} />)}
          </ul>
        ) : store.keys.loaded ? (
          <Empty title="Ключей нет" />
        ) : null}
      </Panel>

      <Panel title="Вебхук"
             actions={
               hook?.url && !store.editingWebhook ? (
                 <>
                   <Button variant="secondary" onClick={() => store.startEditWebhook()}>Изменить</Button>
                   <Button variant="secondary" busy={store.busy}
                           onClick={() => void store.clearWebhook()}>Отключить</Button>
                 </>
               ) : null
             }>
        {store.editingWebhook || !hook?.url ? (
          <div className={styles.intForm}>
            <Field placeholder="https://shop.example.com/webhooks/cryptochangek"
                   value={store.webhookUrl}
                   onChange={(e) => store.setWebhookUrl(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') void store.saveWebhook() }} />
            <Button variant="primary" busy={store.busy}
                    disabled={store.webhookUrl.trim() === ''}
                    onClick={() => void store.saveWebhook()}>
              {hook?.url ? 'Сохранить' : 'Подключить'}
            </Button>
            {store.editingWebhook ? (
              <Button variant="secondary" onClick={() => store.cancelEditWebhook()}>Отмена</Button>
            ) : null}
          </div>
        ) : (
          <ul className={styles.flags}>
            <li className={styles.flag}>
              <span>Адрес приёмника</span>
              <span className={styles.mono}>{hook.url}</span>
            </li>
            <li className={styles.flag}>
              <span>Секрет подписи</span>
              <span className={hook.has_secret ? styles.yes : styles.no}>
                {hook.has_secret ? 'Выдан' : 'Нет'}
              </span>
            </li>
          </ul>
        )}
      </Panel>

      <Panel title="Доставки уведомлений">
        {deliveries && deliveries.items.length > 0 ? (
          <>
            <ul className={styles.intList}>
              {deliveries.items.map((d) => (
                <DeliveryRow key={d.id} item={d} busy={store.busy}
                             onRetry={() => void store.retry(d)} />
              ))}
            </ul>
            <Pagination total={deliveries.total} limit={store.limit}
                        offset={store.deliveriesOffset}
                        onChange={(offset) => store.goDeliveries(offset)} />
          </>
        ) : store.deliveries.loaded ? (
          <Empty title="Доставок не было" />
        ) : null}
      </Panel>

      {store.reveal.kind === 'key' ? (
        <Modal title={`Ключ «${store.reveal.name}»`}
               onClose={() => store.closeReveal()}
               actions={<Button variant="primary" onClick={() => store.closeReveal()}>Готово</Button>}>
          <div className={styles.secretBox}>
            <Strip tone="warn">
              Ключ показывается один раз
            </Strip>
            <SecretValue value={store.reveal.key}
                         onCopy={() => void copy(store.reveal.kind === 'key' ? store.reveal.key : '', 'Ключ скопирован')} />
          </div>
        </Modal>
      ) : null}

      {store.reveal.kind === 'secret' ? (
        <Modal title="Секрет подписи вебхука"
               onClose={() => store.closeReveal()}
               actions={<Button variant="primary" onClick={() => store.closeReveal()}>Готово</Button>}>
          <div className={styles.secretBox}>
            <Strip tone="warn">
              Секрет показывается один раз
            </Strip>
            <span className={styles.intMeta}>Приёмник: {store.reveal.url}</span>
            <SecretValue value={store.reveal.secret}
                         onCopy={() => void copy(store.reveal.kind === 'secret' ? store.reveal.secret : '', 'Секрет скопирован')} />
          </div>
        </Modal>
      ) : null}
    </>
  )
})

function SecretValue({ value, onCopy }: { value: string; onCopy: () => void }) {
  return (
    <div className={styles.intForm}>
      <div className={styles.secretValue}>{value}</div>
      <button type="button" className={styles.copyBtn} aria-label="Скопировать" onClick={onCopy}>
        <CopyIcon />
      </button>
    </div>
  )
}

function KeyRow({ item, onRevoke, busy }: { item: ApiKeyResponse; onRevoke: () => void; busy: boolean }) {
  const revoked = item.revoked_at !== null && item.revoked_at !== undefined
  return (
    <li className={styles.intRow}>
      <span className={styles.intMain}>
        <span className={`${styles.intTitle} ${revoked ? styles.revoked : ''}`}>{item.name}</span>
        <span className={styles.intMeta}>
          <span className={styles.mono}>{item.prefix}…</span>
          {' · '}выдан {when(item.created_at)}
          {' · '}{item.last_used_at ? `использован ${when(item.last_used_at)}` : 'не использовался'}
          {revoked ? ` · отозван ${when(item.revoked_at ?? '')}` : ''}
        </span>
      </span>
      {revoked ? null : (
        <Button variant="secondary" busy={busy} onClick={onRevoke}>Отозвать</Button>
      )}
    </li>
  )
}

const STATUS: Record<WebhookDeliveryResponse['status'], { label: string; cls: string }> = {
  pending: { label: 'В очереди', cls: styles.stPending ?? '' },
  delivered: { label: 'Доставлено', cls: styles.stDelivered ?? '' },
  failed: { label: 'Провалено', cls: styles.stFailed ?? '' },
}

function DeliveryRow({ item, onRetry, busy }: { item: WebhookDeliveryResponse; onRetry: () => void; busy: boolean }) {
  const st = STATUS[item.status]
  return (
    <li className={styles.intRow}>
      <span className={styles.intMain}>
        <span className={styles.intTitle}>
          <span className={styles.mono}>{item.event}</span>{' '}
          <span className={st.cls}>{st.label}</span>
        </span>
        <span className={styles.intMeta}>
          {when(item.created_at)}
          {' · попыток: '}{item.attempts}
          {item.response_status ? ` · HTTP ${item.response_status}` : ''}
          {item.status === 'pending' && item.attempts > 0 ? ` · следующая ${when(item.next_attempt_at)}` : ''}
        </span>
        {item.last_error ? <span className={styles.intMeta}>{item.last_error}</span> : null}
      </span>
      {item.status === 'failed' ? (
        <Button variant="secondary" busy={busy} onClick={onRetry}>Повторить</Button>
      ) : null}
    </li>
  )
}

function when(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(date).replace(', ', ' / ')
}
